import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { StatusApi, getAuthUser } from "@/lib/apiClient";
import { BarChart3, PhoneCall, Mail, ChevronDown, ChevronRight, Loader2, Users, Trash2, AlertTriangle } from "lucide-react";

function thisMonday() {
  const d = new Date();
  const day = (d.getDay() + 6) % 7; // 0 = lunedì
  d.setDate(d.getDate() - day);
  return d.toISOString().slice(0, 10);
}

function fmtDay(d) {
  try { return new Date(d).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" }); } catch { return "—"; }
}
function fmtDayYear(d) {
  try { return new Date(d).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }); } catch { return "—"; }
}
function fmtDateTime(d) {
  try { return new Date(d).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return "—"; }
}

function WeekBlock({ wk, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const [filter, setFilter] = useState("all"); // all | call | email
  const items = wk.items.filter((i) => filter === "all" || i.type === filter);
  const empty = wk.calls === 0 && wk.emails === 0;
  return (
    <div className="border border-border rounded-md overflow-hidden" data-testid={`week-${wk.week_start}`}>
      <button onClick={() => setOpen((o) => !o)} data-testid={`week-toggle-${wk.week_start}`}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/40 transition-colors text-left">
        {open ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-sm flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center justify-center rounded bg-primary/10 text-primary px-2 py-0.5 text-xs font-extrabold" data-testid={`week-num-${wk.week_start}`}>
              Sett. {wk.week_num}{wk.iso_year ? ` · ${wk.iso_year}` : ""}
            </span>
            <span className="text-muted-foreground font-medium text-xs">
              {fmtDay(wk.week_start)} – {fmtDayYear(wk.week_end)}
            </span>
          </div>
          <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
            {Object.entries(wk.by_user).map(([u, v]) => (
              <span key={u} className="inline-flex items-center gap-1">
                <Users className="h-3 w-3" /> {u}: {v.call || 0} tel · {v.email || 0} email
              </span>
            ))}
            {empty && <span className="text-muted-foreground/60">Nessuna attività</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600" title="Telefonate">
            <PhoneCall className="h-3.5 w-3.5" /> {wk.calls}
          </span>
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600" title="Email inviate">
            <Mail className="h-3.5 w-3.5" /> {wk.emails}
          </span>
        </div>
      </button>
      {open && !empty && (
        <div className="border-t border-border">
          <div className="flex items-center gap-1.5 px-4 py-2 bg-muted/30">
            {[["all", "Tutte"], ["call", "Telefonate"], ["email", "Email"]].map(([k, lbl]) => (
              <button key={k} onClick={() => setFilter(k)} data-testid={`week-filter-${wk.week_start}-${k}`}
                className={`text-[11px] px-2 py-1 rounded border transition-colors ${filter === k ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted"}`}>
                {lbl}
              </button>
            ))}
            <span className="ml-auto text-[11px] text-muted-foreground">{items.length} aziende</span>
          </div>
          <ul className="divide-y divide-border max-h-72 overflow-auto" data-testid={`week-items-${wk.week_start}`}>
            {items.map((it, i) => (
              <li key={i} className="flex items-center gap-2 px-4 py-2 text-sm">
                {it.type === "call"
                  ? <PhoneCall className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                  : <Mail className="h-3.5 w-3.5 text-blue-600 shrink-0" />}
                <span className="font-medium truncate flex-1">{it.company_name || "—"}</span>
                {it.outcome && <span className="text-xs text-muted-foreground truncate max-w-[180px] hidden sm:block">{it.outcome}</span>}
                <span className="text-[11px] text-muted-foreground shrink-0">{it.user_name}</span>
                <span className="text-[11px] text-muted-foreground shrink-0 w-24 text-right">{fmtDateTime(it.at)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function StatsPanel({ open, onOpenChange }) {
  const [data, setData] = useState(null);
  const isAdmin = getAuthUser()?.role === "admin";
  const [showReset, setShowReset] = useState(false);
  const [before, setBefore] = useState(thisMonday());
  const [confirming, setConfirming] = useState(false);
  const [resetting, setResetting] = useState(false);

  const load = () => { setData(null); StatusApi.weeklyStats(8).then(setData).catch(() => setData({ weeks: [] })); };
  useEffect(() => { if (open) load(); }, [open]);

  const doReset = async () => {
    setResetting(true);
    try {
      const r = await StatusApi.resetStats(before);
      const d = r.deleted || {};
      toast.success(`Dati di prova azzerati: ${d.attivita_chiamate_email || 0} attività, ${d.invii_campagne || 0} invii, ${d.richieste_form || 0} richieste.`);
      setConfirming(false);
      setShowReset(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante l'azzeramento");
    } finally {
      setResetting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="stats-panel">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-primary" /> Statistiche settimanali
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground shrink-0">Aziende contattate ogni settimana (lunedì → sabato), tra telefonate ed email.</p>
        <div className="overflow-auto flex-1 space-y-2 mt-1">
          {data === null ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-16 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" /> Caricamento...
            </div>
          ) : data.weeks.length === 0 ? (
            <div className="text-sm text-muted-foreground py-16 text-center" data-testid="stats-empty">
              Nessuna attività registrata ancora. Registra chiamate o invia email per popolare le statistiche.
            </div>
          ) : (
            data.weeks.map((wk, i) => <WeekBlock key={wk.week_start} wk={wk} defaultOpen={i === 0} />)
          )}
        </div>

        {isAdmin && (
          <div className="shrink-0 border-t border-border pt-3 mt-1">
            {!showReset ? (
              <button onClick={() => setShowReset(true)} data-testid="open-reset-stats"
                className="text-xs text-muted-foreground hover:text-red-600 inline-flex items-center gap-1.5 transition-colors">
                <Trash2 className="h-3.5 w-3.5" /> Azzera i dati di prova (solo admin)
              </button>
            ) : (
              <div className="rounded-md border border-red-200 bg-red-50 p-3" data-testid="reset-stats-box">
                <div className="flex items-start gap-2 text-sm text-red-800">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-semibold">Azzera statistiche di prova</p>
                    <p className="text-xs text-red-700 mt-0.5">Elimina definitivamente chiamate, email registrate, invii/aperture/click delle campagne, richieste dai form e visite <b>precedenti</b> alla data scelta. Gli stati e le note delle aziende <b>non</b> vengono toccati.</p>
                  </div>
                </div>
                <div className="flex flex-wrap items-end gap-3 mt-3">
                  <div>
                    <label className="block text-[11px] font-semibold text-red-800 mb-1">Mantieni i dati a partire dal</label>
                    <Input type="date" value={before} onChange={(e) => { setBefore(e.target.value); setConfirming(false); }}
                      data-testid="reset-before-date" className="h-8 w-40 bg-white" />
                  </div>
                  {!confirming ? (
                    <>
                      <Button size="sm" variant="destructive" className="h-8" onClick={() => setConfirming(true)} data-testid="reset-stats-btn">
                        Azzera dati prima del {before.split("-").reverse().join("/")}
                      </Button>
                      <button onClick={() => setShowReset(false)} className="text-xs text-muted-foreground hover:underline h-8">Annulla</button>
                    </>
                  ) : (
                    <>
                      <span className="text-xs font-semibold text-red-800">Sei sicuro? L'operazione è irreversibile.</span>
                      <Button size="sm" variant="destructive" className="h-8" disabled={resetting} onClick={doReset} data-testid="reset-stats-confirm">
                        {resetting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sì, azzera"}
                      </Button>
                      <button onClick={() => setConfirming(false)} className="text-xs text-muted-foreground hover:underline h-8">No, annulla</button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
